import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CheckCircle, XCircle, DollarSign, Edit, Mail, MessageSquare, Paperclip, Clock, Skull } from 'lucide-react';
import { useApplications, useApplicationActions } from '../hooks/useApplications';
import { formatCurrency } from '../lib/formatters';
import { ReviewDialog, ConvertDialog, EditDialog, StatusDialog, NoteDialog, DocumentsDialog } from '../components/ApplicationDialogs';

const ApplicationReview = () => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialStatus = searchParams.get('status') || 'all';
  const { filteredApplications, loading, activeTab, setActiveTab, statusCounts: counts, refetch } = useApplications(getAuthHeaders, initialStatus);
  const { quickStatusChange: handleQuickStatusChange, addNote, updateApplication, reviewApplication, convertToDeal, changeStatus } = useApplicationActions(getAuthHeaders, refetch);

  const [selectedApp, setSelectedApp] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewData, setReviewData] = useState({ status: '', rejection_reason: '', approved_amount: '', factor_rate: '', term_days: '', commission_rate: '' });
  const [editFormData, setEditFormData] = useState({});
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [sendEmailNotification, setSendEmailNotification] = useState(true);
  const [noteContent, setNoteContent] = useState('');
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [viewNotesDialogOpen, setViewNotesDialogOpen] = useState(false);

  const isAdmin = user?.roles?.includes('admin');
  const isUnderwriter = user?.roles?.includes('underwriter');

  useEffect(() => { if (user && !isAdmin && !isUnderwriter) navigate('/'); }, [user, isAdmin, isUnderwriter, navigate]);
  useEffect(() => { const sp = searchParams.get('status'); if (sp) setActiveTab(sp); }, [searchParams, setActiveTab]);

  const handleStatusChange = async () => {
    if (!selectedApp || !newStatus) return;
    if (await changeStatus(selectedApp.id, newStatus, statusNotes, sendEmailNotification)) {
      setStatusDialogOpen(false); setNewStatus(''); setStatusNotes(''); setSendEmailNotification(true);
    }
  };
  const handleAddNote = async () => {
    if (!selectedApp || !noteContent.trim()) return;
    if (await addNote(selectedApp.id, noteContent)) { setNoteDialogOpen(false); setNoteContent(''); }
  };
  const handleEditApplication = async () => {
    if (!selectedApp) return;
    if (await updateApplication(selectedApp.id, editFormData)) setEditDialogOpen(false);
  };
  const openEditDialog = (app) => {
    setSelectedApp(app);
    setEditFormData({
      business_name: app.business_name || '', dba: app.dba || '', ein_tax_id: app.ein_tax_id || '',
      business_type: app.business_type || '', industry: app.industry || '', years_in_business: app.years_in_business || '',
      monthly_revenue: app.monthly_revenue || '', owner_name: app.owner_name || '', owner_ssn: app.owner_ssn || '',
      owner_dob: app.owner_dob || '', owner_address: app.owner_address || '', owner_phone: app.owner_phone || '',
      owner_email: app.owner_email || '', requested_amount: app.requested_amount || '', purpose_of_funds: app.purpose_of_funds || '', notes: app.notes || '',
    });
    setEditDialogOpen(true);
  };
  const handleReviewSubmit = async () => {
    if (!selectedApp) return;
    if (await reviewApplication(selectedApp.id, reviewAction, reviewData.rejection_reason)) {
      setReviewDialogOpen(false); setReviewData({ ...reviewData, rejection_reason: '' });
    }
  };
  const handleConvertToDeal = async () => {
    if (!selectedApp) return;
    if (await convertToDeal(selectedApp, reviewData)) {
      setConvertDialogOpen(false);
      setReviewData({ status: '', rejection_reason: '', approved_amount: '', factor_rate: '', term_days: '', commission_rate: '' });
    }
  };

  const getStatusBadge = (status) => {
    const colors = { 'Pending': 'bg-yellow-100 text-yellow-800', 'Under Review': 'bg-blue-100 text-blue-800', 'Approved': 'bg-green-100 text-green-800', 'Rejected': 'bg-red-100 text-red-800', 'Declined': 'bg-red-100 text-red-800', 'Killed': 'bg-gray-800 text-white', 'On Hold': 'bg-orange-100 text-orange-800', 'More Info Needed': 'bg-purple-100 text-purple-800' };
    return <Badge className={colors[status] || 'bg-gray-100'}>{status}</Badge>;
  };
  const allStatuses = ['Pending', 'Under Review', 'Approved', 'Declined', 'Killed', 'On Hold', 'More Info Needed'];

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div></Layout>;

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Applications</h1>
          <p className="text-gray-600">Review and process incoming deal applications from ISOs</p>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => { setActiveTab(value); navigate(`/applications${value !== 'all' ? `?status=${value}` : ''}`, { replace: true }); }} className="mb-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="all" className="flex items-center gap-2">All<Badge variant="secondary">{counts.all}</Badge></TabsTrigger>
            <TabsTrigger value="to-review" className="flex items-center gap-2"><Clock className="h-4 w-4" />To Review{counts.toReview > 0 && <Badge className="bg-orange-500 text-white">{counts.toReview}</Badge>}</TabsTrigger>
            <TabsTrigger value="approved" className="flex items-center gap-2"><CheckCircle className="h-4 w-4" />Approved{counts.approved > 0 && <Badge className="bg-green-500 text-white">{counts.approved}</Badge>}</TabsTrigger>
            <TabsTrigger value="declined" className="flex items-center gap-2"><XCircle className="h-4 w-4" />Declined{counts.declined > 0 && <Badge className="bg-red-500 text-white">{counts.declined}</Badge>}</TabsTrigger>
            <TabsTrigger value="killed" className="flex items-center gap-2"><Skull className="h-4 w-4" />Killed{counts.killed > 0 && <Badge className="bg-gray-800 text-white">{counts.killed}</Badge>}</TabsTrigger>
          </TabsList>
        </Tabs>

        <Card>
          <CardHeader><CardTitle>{activeTab === 'all' ? 'All Applications' : activeTab === 'to-review' ? 'Applications To Review' : activeTab === 'approved' ? 'Approved Applications' : activeTab === 'declined' ? 'Declined Applications' : 'Killed Applications'}</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Submitted By</TableHead><TableHead>Business</TableHead><TableHead>Owner</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredApplications.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No applications found</TableCell></TableRow>
                ) : filteredApplications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell>{new Date(app.submission_date).toLocaleDateString()}</TableCell>
                    <TableCell>{app.submitted_by_name}</TableCell>
                    <TableCell className="font-medium">{app.business_name}</TableCell>
                    <TableCell>{app.owner_name}</TableCell>
                    <TableCell>{formatCurrency(app.requested_amount)}</TableCell>
                    <TableCell>
                      <Select value={app.status} onValueChange={(ns) => handleQuickStatusChange(app, ns)}>
                        <SelectTrigger className="w-[140px] h-8"><SelectValue>{getStatusBadge(app.status)}</SelectValue></SelectTrigger>
                        <SelectContent>{allStatuses.map(s => <SelectItem key={s} value={s}>{getStatusBadge(s)}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEditDialog(app)}><Edit className="h-4 w-4 mr-1" />Edit</Button>
                        <Button size="sm" variant="outline" onClick={() => { setSelectedApp(app); setNewStatus(app.status); setStatusDialogOpen(true); }}><Mail className="h-4 w-4 mr-1" />Status</Button>
                        <Button size="sm" variant="outline" onClick={() => { setSelectedApp(app); setNoteDialogOpen(true); }}><MessageSquare className="h-4 w-4 mr-1" />Note{app.underwriter_notes?.length > 0 && <Badge variant="secondary" className="ml-1 px-1.5 py-0">{app.underwriter_notes.length}</Badge>}</Button>
                        {app.application_documents?.length > 0 && <Button size="sm" variant="outline" onClick={() => { setSelectedApp(app); setViewNotesDialogOpen(true); }}><Paperclip className="h-4 w-4 mr-1" />Docs ({app.application_documents.length})</Button>}
                        {app.status === 'Pending' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => { setSelectedApp(app); setReviewAction('approve'); setReviewDialogOpen(true); }}><CheckCircle className="h-4 w-4 mr-1 text-green-600" />Approve</Button>
                            <Button size="sm" variant="outline" onClick={() => { setSelectedApp(app); setReviewAction('reject'); setReviewDialogOpen(true); }}><XCircle className="h-4 w-4 mr-1 text-red-600" />Reject</Button>
                          </>
                        )}
                        {app.status === 'Approved' && !app.approved_deal_id && <Button size="sm" onClick={() => { setSelectedApp(app); setConvertDialogOpen(true); }}><DollarSign className="h-4 w-4 mr-1" />Convert to Deal</Button>}
                        {app.approved_deal_id && <Badge className="bg-purple-100 text-purple-800">Converted to Deal</Badge>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <ReviewDialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen} selectedApp={selectedApp} reviewAction={reviewAction} reviewData={reviewData} setReviewData={setReviewData} onSubmit={handleReviewSubmit} />
        <ConvertDialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen} selectedApp={selectedApp} reviewData={reviewData} setReviewData={setReviewData} onSubmit={handleConvertToDeal} />
        <EditDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} selectedApp={selectedApp} editFormData={editFormData} setEditFormData={setEditFormData} onSubmit={handleEditApplication} />
        <StatusDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen} selectedApp={selectedApp} newStatus={newStatus} setNewStatus={setNewStatus} statusNotes={statusNotes} setStatusNotes={setStatusNotes} sendEmailNotification={sendEmailNotification} setSendEmailNotification={setSendEmailNotification} onSubmit={handleStatusChange} />
        <NoteDialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen} selectedApp={selectedApp} noteContent={noteContent} setNoteContent={setNoteContent} onSubmit={handleAddNote} />
        <DocumentsDialog open={viewNotesDialogOpen} onOpenChange={setViewNotesDialogOpen} selectedApp={selectedApp} />
      </div>
    </Layout>
  );
};

export default ApplicationReview;
